/*
 * Hides the purchase buttons for visitors in the EU and the UK (D-20).
 *
 * Why this exists: removing those countries from Markets does not stop them buying.
 * A download has no shipping address, and checkout offers every billing country
 * regardless of markets (measured on prod 2026-10-02: Germany reached the payment
 * step at ¥4,700). Selling there would put the seller under EU/UK VAT from the first
 * sale. Blocking the billing country at checkout needs a Checkout Validation function,
 * which Basic can only get from a public App Store app.
 *
 * So this is access control by visitor location, not a guarantee: a VPN gets past it,
 * and so does going to /checkout directly. It stops the ordinary case.
 *
 * Location comes from /browsing_context_suggestions.json, the endpoint Shopify's own
 * geolocation uses. It reports the IP country even for countries outside every market,
 * which `localization.country` does not. It is not a documented API, so every failure
 * leaves the buttons in place: a Japanese customer locked out by a broken lookup is the
 * worse outcome.
 *
 * Markup contract: elements with [data-region-gated] are hidden for blocked visitors,
 * elements with [data-region-notice] are shown instead.
 */
(() => {
  // Same set as the countries removed from the International market (LAUNCH-PLAN D-20):
  // the EU 27, the UK, and territories inside their VAT areas, kept on the safe side.
  const BLOCKED = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
    'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    'GB', 'IM', 'MC', 'AX', 'GP', 'MQ', 'GF', 'RE', 'YT',
  ]);
  const CACHE_KEY = 'region-gate:country';

  // Test hook: ?region_gate=DE forces the blocked state. It can only add blocking,
  // never remove it, so it is not a way around the gate.
  function forcedCountry() {
    const value = new URLSearchParams(location.search).get('region_gate');
    return value && BLOCKED.has(value.toUpperCase()) ? value.toUpperCase() : null;
  }

  function readCache() {
    try {
      return sessionStorage.getItem(CACHE_KEY);
    } catch {
      return null;
    }
  }

  function writeCache(country) {
    try {
      sessionStorage.setItem(CACHE_KEY, country);
    } catch {
      // Storage blocked: we simply ask again on the next page.
    }
  }

  async function detectCountry() {
    const cached = readCache();
    if (cached) return cached;

    const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', {
      credentials: 'same-origin',
    });
    if (!response.ok) return null;

    const data = await response.json();
    const country = data?.detected_values?.country?.handle;
    if (typeof country !== 'string' || country.length !== 2) return null;

    writeCache(country);
    return country;
  }

  function block() {
    for (const el of document.querySelectorAll('[data-region-gated]')) {
      el.hidden = true;
      for (const control of el.querySelectorAll('button, input[type="submit"]')) {
        control.disabled = true;
      }
    }
    for (const el of document.querySelectorAll('[data-region-notice]')) {
      el.hidden = false;
    }
    document.documentElement.dataset.regionBlocked = 'true';
  }

  async function run() {
    if (!document.querySelector('[data-region-gated]')) return;

    const country = forcedCountry() ?? (await detectCountry());
    if (country && BLOCKED.has(country)) block();
  }

  run().catch(() => {
    // Fail open (see header).
  });
})();
